import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Engineering } from '@mui/icons-material';
import api from '../../utils/api';
import { BuildingCaretaker } from '../../types';
import PageHeader from '../../components/Common/PageHeader';
import AddBuildingCaretakerDialog from '../../components/BuildingCaretakers/AddBuildingCaretakerDialog';
import EditBuildingCaretakerDialog from '../../components/BuildingCaretakers/EditBuildingCaretakerDialog';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import ToastNotification from '../../utils/toastNotification';

const BuildingCaretakersPage: React.FC = () => {

  const [caretakers, setCaretakers] = useState<BuildingCaretaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCaretaker, setSelectedCaretaker] = useState<BuildingCaretaker | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caretakerToDelete, setCaretakerToDelete] = useState<BuildingCaretaker | null>(null);

  useEffect(() => {
    loadCaretakers();
  }, []);

  const loadCaretakers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/building-caretakers');
      setCaretakers(response.data);
    } catch (error) {
      console.error('Error loading building caretakers:', error);
      ToastNotification.error('Failed to load building caretakers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (caretaker: BuildingCaretaker) => {
    setSelectedCaretaker(caretaker);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (caretaker: BuildingCaretaker) => {
    setCaretakerToDelete(caretaker);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!caretakerToDelete) return;
    try {
      await api.delete(`/building-caretakers/${caretakerToDelete.id}`);
      ToastNotification.success('Building caretaker deleted successfully');
      await loadCaretakers();
    } catch (error) {
      console.error('Error deleting caretaker:', error);
      ToastNotification.error('Failed to delete building caretaker');
    } finally {
      setDeleteDialogOpen(false);
      setCaretakerToDelete(null);
    }
  };

  const getColumns = (): GridColDef[] => [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'buildingName',
      headerName: 'Building',
      flex: 1,
      minWidth: 140,
      renderCell: (params) =>
        params.row.buildingName || (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'governorate',
      headerName: 'Governorate',
      width: 150,
      renderCell: (params) =>
        params.row.governorate ? (
          <Chip label={params.row.governorate} size="small" variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'district',
      headerName: 'District',
      width: 140,
      renderCell: (params) =>
        params.row.district || (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'town',
      headerName: 'Town',
      width: 130,
      renderCell: (params) =>
        params.row.town || (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}
        >
          {params.row.notes || '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(params.row)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Building Caretakers"
        subtitle="Manage building caretakers, their contact info, and locations"
        icon={<Engineering sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Building Caretakers' },
        ]}
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={() => setAddDialogOpen(true)}
            sx={{
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': { boxShadow: 4 },
            }}
          >
            Add Caretaker
          </Button>
        }
      />

      <Paper
        sx={{
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <DataGrid
          rows={caretakers}
          columns={getColumns()}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            minHeight: 500,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f8f9fa',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9f9f9' },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
          }}
        />
      </Paper>

      <AddBuildingCaretakerDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={loadCaretakers}
      />

      <EditBuildingCaretakerDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedCaretaker(null);
        }}
        onSuccess={loadCaretakers}
        caretaker={selectedCaretaker}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Building Caretaker"
        message={`Are you sure you want to delete caretaker "${caretakerToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setCaretakerToDelete(null);
        }}
      />
    </Box>
  );
};

export default BuildingCaretakersPage;
